import React, { useState, useEffect, useRef } from "react";
import select_arrow_black from "../../../images/select-arrow.svg";
import "./YearMonthPicker.css"; // We'll create this CSS file

const YearMonthPicker = ({
  value,
  onChange,
  placeholder = "Select Month & Year",
  disabled = false,
  language = "en",
  minDate = null, // Optional: minimum selectable date
  maxDate = null, // Optional: maximum selectable date
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const dropdownRef = useRef(null);

  // Generate years (from current year back to 50 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 51 }, (_, i) => currentYear - i);

  // Month names in both languages
  const months = [
    { value: 1, name_en: "January", name_ar: "يناير" },
    { value: 2, name_en: "February", name_ar: "فبراير" },
    { value: 3, name_en: "March", name_ar: "مارس" },
    { value: 4, name_en: "April", name_ar: "أبريل" },
    { value: 5, name_en: "May", name_ar: "مايو" },
    { value: 6, name_en: "June", name_ar: "يونيو" },
    { value: 7, name_en: "July", name_ar: "يوليو" },
    { value: 8, name_en: "August", name_ar: "أغسطس" },
    { value: 9, name_en: "September", name_ar: "سبتمبر" },
    { value: 10, name_en: "October", name_ar: "أكتوبر" },
    { value: 11, name_en: "November", name_ar: "نوفمبر" },
    { value: 12, name_en: "December", name_ar: "ديسمبر" },
  ];

  // Initialize from value prop
  useEffect(() => {
    if (typeof value === "number") {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentFullYear = currentDate.getFullYear();

      let targetMonth = currentMonth - value;
      let targetYear = currentFullYear;

      while (targetMonth < 0) {
        targetMonth += 12;
        targetYear -= 1;
      }

      setSelectedYear(targetYear);
      setSelectedMonth(targetMonth + 1);
    } else if (value instanceof Date && !isNaN(value)) {
      setSelectedYear(value.getFullYear());
      setSelectedMonth(value.getMonth() + 1);
    } else {
      setSelectedYear(null);
      setSelectedMonth(null);
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleYearSelect = (year) => {
    setSelectedYear(year);

    // Reset to January when changing year to avoid invalid month selections
    setSelectedMonth(1);

    // Create new date with January of selected year
    const newDate = new Date(year, 0, 1); // January is month 0
    onChange(newDate);
  };

  const handleMonthSelect = (month) => {
    setSelectedMonth(month);
    if (selectedYear) {
      const newDate = new Date(selectedYear, month - 1, 1);
      onChange(newDate);
      setIsOpen(false);
    }
  };

  const getDisplayText = () => {
    if (selectedYear && selectedMonth) {
      const monthName = months.find((m) => m.value === selectedMonth);
      return language === "en"
        ? `${monthName.name_en} ${selectedYear}`
        : `${monthName.name_ar} ${selectedYear}`;
    }
    return placeholder;
  };

  const isDateDisabled = (year, month) => {
    if (!minDate && !maxDate) return false;

    const date = new Date(year, month - 1, 1);

    if (minDate && date < new Date(minDate)) return true;
    if (maxDate && date > new Date(maxDate)) return true;

    return false;
  };

  console.log("selectedYear: ", selectedYear);
  console.log("selectedMonth: ", selectedMonth);
  console.log("placeholder: ", placeholder);
  console.log("value: ", value);

  return (
    <div className="year-month-picker" ref={dropdownRef}>
      <div
        className={`picker-input ${isOpen ? "open" : ""} ${
          disabled ? "disabled" : ""
        }`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span
          className={selectedYear && selectedMonth ? "selected" : "placeholder"}
        >
          {getDisplayText()}
        </span>
        <img
          src={select_arrow_black}
          alt="dropdown arrow"
          className={`arrow ${isOpen ? "up" : "down"}`}
        />
      </div>

      {isOpen && !disabled && (
        <div className="picker-dropdown">
          <div className="picker-content">
            <div className="year-section">
              <h4>{language === "en" ? "Year" : "السنة"}</h4>
              <div className="year-grid">
                {years.map((year) => (
                  <div
                    key={year}
                    className={`year-item ${
                      selectedYear === year ? "selected" : ""
                    }`}
                    onClick={() => handleYearSelect(year)}
                  >
                    {year}
                  </div>
                ))}
              </div>
            </div>

            <div className="month-section">
              <h4>{language === "en" ? "Month" : "الشهر"}</h4>
              <div className="month-grid">
                {months.map((month) => (
                  <div
                    key={month.value}
                    className={`month-item ${
                      selectedMonth === month.value ? "selected" : ""
                    } ${
                      selectedYear && isDateDisabled(selectedYear, month.value)
                        ? "disabled"
                        : ""
                    }`}
                    onClick={() => {
                      if (
                        !selectedYear ||
                        !isDateDisabled(selectedYear, month.value)
                      ) {
                        handleMonthSelect(month.value);
                      }
                    }}
                  >
                    {language === "en" ? month.name_en : month.name_ar}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YearMonthPicker;
